// src/components/RegionBoneQuality.js

import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from '@mui/material';

import { BONE_QUALITY_OPTIONS, REGION_FORM_STYLES } from './RegionConstants';
import { 
    shouldShowBuccal, 
    shouldShowLabial, 
    isMandibleRegion, 
    isMaxillaRegion 
} from './RegionUtils';

/**
 * RegionBoneQuality Component
 * 
 * Handles bone quality assessment form with conditional rendering
 * based on region type and jaw location.
 * 
 * @param {Object} props - Component props
 * @param {string|number} props.regionNumber - Region number or identifier
 * @param {string} props.reportType - Report type (Mandible/Maxilla)
 * @param {Object} props.boneQuality - Current bone quality values
 * @param {Function} props.onBoneQualityChange - Callback for bone quality changes
 * @param {boolean} props.disabled - Whether the form is disabled
 */
const RegionBoneQuality = ({
    regionNumber,
    reportType,
    boneQuality,
    onBoneQualityChange,
    onOtherCommentChange,
    disabled = false
}) => {
    // Determine which bone quality options to show based on region
    const showBuccal = shouldShowBuccal(regionNumber);
    const showLabial = shouldShowLabial(regionNumber);
    const isMandible = isMandibleRegion(reportType, regionNumber);
    const isMaxilla = isMaxillaRegion(reportType, regionNumber);


    /**
     * Handle bone quality dropdown changes
     * @param {Event} event - Select change event
     */
    const handleBoneQualityChange = (event) => {
        const { name, value } = event.target;
        
        
        if (onBoneQualityChange) {
            onBoneQualityChange(name, value);
        }
    };
    const handleOtherCommentChange = (event) => {
        const { name, value } = event.target;
        if (onOtherCommentChange) {
            onOtherCommentChange(name, value);
        }
    };

    /**
     * Render a bone quality dropdown
     * @param {string} boneType - Type of bone (crestal, buccal, etc.)
     * @param {string} label - Display label for the dropdown
     * @param {Array} options - Array of options for this bone type
     * @returns {JSX.Element} FormControl with Select
     */
    const renderBoneQualitySelect = (boneType, label, options) => (
        <FormControl fullWidth sx={{ mb: 2 }} key={boneType}>
            <InputLabel>{label}</InputLabel>
            <Select
                name={boneType}
                value={boneQuality[boneType] || '___________'}
                label={label}
                onChange={handleBoneQualityChange}
                disabled={disabled}
            >
                {options.map(option => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </Select>
            {boneQuality[boneType] === 'Other' && (
                <TextField
                    name={boneType}
                    label={`Other Comments for ${label}`}
                    value={(boneQuality.otherComments && boneQuality.otherComments[boneType]) || ''}
                    onChange={handleOtherCommentChange}
                    fullWidth
                    margin="normal"
                />
            )}
        </FormControl>
    );

    return (
        <Box sx={REGION_FORM_STYLES.sectionStyle}>
            <Typography sx={REGION_FORM_STYLES.sectionTitleStyle}>
                BONE QUALITY
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Crestal Bone - Always shown */}
                {renderBoneQualitySelect(
                    'crestal',
                    'Choose Crestal Bone Quality:',
                    BONE_QUALITY_OPTIONS.crestal
                )}
                
                {/* Buccal Bone - For posterior regions and pterygoid */}
                {showBuccal && renderBoneQualitySelect(
                    'buccal',
                    'Choose Buccal Bone Quality:',
                    BONE_QUALITY_OPTIONS.buccal
                )}
                
                {/* Labial Bone - For anterior regions */}
                {showLabial && renderBoneQualitySelect(
                    'labial',
                    'Choose Labial Bone Quality:',
                    BONE_QUALITY_OPTIONS.labial
                )}
                
                {/* Lingual Bone - For mandible regions */}
                {isMandible && renderBoneQualitySelect(
                    'lingual',
                    'Choose Lingual Bone Quality:',
                    BONE_QUALITY_OPTIONS.lingual
                )}
                
                {/* Palatal Bone - For maxilla regions */}
                {isMaxilla && renderBoneQualitySelect(
                    'palatal',
                    'Choose Palatal Bone Quality:',
                    BONE_QUALITY_OPTIONS.palatal
                )}
                
                {/* Cancellous Bone - Always shown */}
                {renderBoneQualitySelect(
                    'cancellous',
                    'Choose Cancellous Bone Quality:',
                    BONE_QUALITY_OPTIONS.cancellous
                )}
            </Box>

            {/* Validation Summary */}
            {boneQuality && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                        Bone Quality Completion: {
                            Object.values(boneQuality).filter(value => 
                                typeof value === 'string' && value && value !== '___________' && value.trim() !== ''
                            ).length
                        } of {
                            [
                                'crestal',
                                showBuccal && 'buccal',
                                showLabial && 'labial', 
                                isMandible && 'lingual',
                                isMaxilla && 'palatal',
                                'cancellous'
                            ].filter(Boolean).length
                        } fields completed
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

RegionBoneQuality.propTypes = {
    regionNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    reportType: PropTypes.string.isRequired,
    boneQuality: PropTypes.shape({
        crestal: PropTypes.string,
        buccal: PropTypes.string,
        lingual: PropTypes.string,
        palatal: PropTypes.string,
        labial: PropTypes.string,
        cancellous: PropTypes.string,
        otherComments: PropTypes.object 
    }).isRequired,
    onBoneQualityChange: PropTypes.func.isRequired,
    onOtherCommentChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};

export default RegionBoneQuality;
